import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, Mail, Eye, EyeOff, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("admin_token", res.data.token);
      localStorage.setItem("admin_name", res.data.name);
      localStorage.setItem("admin_email", res.data.email);
      navigate("/admin");
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F5F0E8] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/" className="inline-block">
            <img
              src="https://customer-assets.emergentagent.com/job_kacinoe-health-shop/artifacts/d94t0l0x_Logo%20Sante%20Kacinoe.png"
              alt="Santé KACINOÉ"
              className="h-24 w-auto mx-auto"
            />
          </a>
          <p className="text-stone-500 mt-3 text-sm">Espace Administration</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
          <h1
            className="text-2xl font-bold text-stone-900 mb-6 text-center"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Connexion
          </h1>

          {error && (
            <div data-testid="login-error" className="flex items-center gap-2 bg-red-50 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm text-stone-600 mb-1.5 font-medium">Email</label>
              <div className="relative">
                <Mail size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@kacinoe.com"
                  className="w-full pl-11 pr-4 py-3 rounded-xl border border-stone-200 focus:border-[#6C3FC5] focus:ring-2 focus:ring-[#6C3FC5]/20 outline-none text-sm transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-stone-600 mb-1.5 font-medium">Mot de passe</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-11 pr-12 py-3 rounded-xl border border-stone-200 focus:border-[#6C3FC5] focus:ring-2 focus:ring-[#6C3FC5]/20 outline-none text-sm transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-[#6C3FC5] hover:bg-[#582FB0] text-white py-3 rounded-full text-sm font-medium transition-all duration-200 hover:scale-[1.02] shadow-lg shadow-[#6C3FC5]/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Connexion..." : "Se connecter"}
            </button>
          </form>
        </div>

        <p className="text-center text-stone-400 text-xs mt-6">
          <a href="/" className="hover:text-[#6C3FC5] transition-colors">
            Retour au site
          </a>
        </p>
      </div>
    </div>
  );
}
